import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import { Manulife, ManulifeFr } from '@manulife/mux-logos';
import PropTypes from '../../../node_modules/prop-types/index.js';
import Icon from '../Icon/Icon.js';
import TextLink from '../TextLink/TextLink.js';
import i18n from '../../lang/i18n/index.js';
import LANG from '../../lang/lang.js';
import { safeRest, rem } from '../../utils/functions.js';
import { DEFAULT_FOOTER_LOGO_STYLE, SOCIAL_MEDIA } from './constants.js';
import LinkDropdown from './LinkDropdown.js';
import { FooterWrapper, FooterTop, FooterMiddle, SiteLinkList, SiteLink, SocialMediaLinks, ScreenReaderOnly, FooterBottom, CopyrightYear } from './styledComponents.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "items", "copyrightText", "copyrightYear", "copyrightYearAria", "logo", "quickLinks", "socialMediaLinks"];
var Footer = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var controlledId = _ref.id,
      items = _ref.items,
      copyrightText = _ref.copyrightText,
      copyrightYear = _ref.copyrightYear,
      copyrightYearAria = _ref.copyrightYearAria,
      logo = _ref.logo,
      quickLinks = _ref.quickLinks,
      socialMediaLinks = _ref.socialMediaLinks,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useDerivedState = useDerivedState(useId('footer-id-'), controlledId),
      _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
      id = _useDerivedState2[0];

  var safeProps = safeRest(rest);
  var lang = useLang();
  var theme = useTheme();
  var DefaultLogo = lang === LANG.EN_CA ? Manulife : ManulifeFr; // NOSONAR

  return /*#__PURE__*/React.createElement(FooterWrapper, _extends({}, safeProps, {
    id: id,
    theme: theme,
    ref: ref,
    tabIndex: -1
  }), /*#__PURE__*/React.createElement("div", null, (quickLinks === null || quickLinks === void 0 ? void 0 : quickLinks.length) > 0 && /*#__PURE__*/React.createElement(FooterTop, {
    theme: theme
  }, /*#__PURE__*/React.createElement(LinkDropdown, {
    id: "quick-links",
    title: i18n[lang].footer.quickLinksHeader,
    links: quickLinks
  })), /*#__PURE__*/React.createElement(FooterMiddle, {
    theme: theme
  }, (items === null || items === void 0 ? void 0 : items.length) > 0 && /*#__PURE__*/React.createElement(SiteLinkList, {
    lang: lang,
    "data-testid": "sitelink-list"
  }, items.map(function (elem) {
    var children = elem.icon ? /*#__PURE__*/React.createElement(React.Fragment, null, elem.title, elem.icon) : elem.title;
    var ariaProps = elem.icon ? {
      'aria-label': elem.title
    } : {};
    return /*#__PURE__*/React.createElement(SiteLink, {
      theme: theme,
      lang: lang,
      key: elem.id,
      "data-testid": "sitelink-".concat(elem.id)
    }, /*#__PURE__*/React.createElement(TextLink, _extends({
      id: "".concat(id, "-a-").concat(elem.id),
      to: elem.href,
      isExternal: elem.isExternal
    }, ariaProps), children));
  })), (socialMediaLinks === null || socialMediaLinks === void 0 ? void 0 : socialMediaLinks.length) > 0 && /*#__PURE__*/React.createElement(SocialMediaLinks, {
    "data-testid": "social-media-links",
    theme: theme
  }, socialMediaLinks.map(function (link) {
    return /*#__PURE__*/React.createElement("li", {
      key: "social-".concat(link.site)
    }, /*#__PURE__*/React.createElement("a", {
      id: "social-a-".concat(link.site),
      href: link.href,
      target: "_blank",
      rel: "noopener noreferrer"
    }, /*#__PURE__*/React.createElement(Icon, {
      manulifeName: link.site,
      fill: theme.colorPalette.neutralColors.light.shade_dark_3,
      alt: link.site,
      height: rem(16),
      width: rem(16)
    }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "".concat(link.site, ", "), i18n[lang].footer.externalLinkText)));
  }))), /*#__PURE__*/React.createElement(FooterBottom, {
    theme: theme
  }, logo ? logo : /*#__PURE__*/React.createElement(DefaultLogo.REVERSE, {
    title: i18n[lang].footer.logoAltText,
    style: DEFAULT_FOOTER_LOGO_STYLE
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(CopyrightYear, {
    id: "".concat(id, "-copyright-year"),
    ariaLabel: copyrightYear === copyrightYearAria ? undefined : copyrightYearAria
  }, copyrightYear), ' '
  /* the linter treats this as an empty statement without this comment */
  , /*#__PURE__*/React.createElement("span", {
    id: "".concat(id, "-copyright-text")
  }, copyrightText || i18n[lang].footer.defaultCopyrightText)))));
});
process.env.NODE_ENV !== "production" ? Footer.propTypes = {
  /** ID for footer */
  id: PropTypes.string,

  /** An array of items to display in the links section of the footer */
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    href: PropTypes.string.isRequired,
    icon: PropTypes.node,
    isExternal: PropTypes.bool
  })),

  /** Text for footer copyright */
  copyrightText: PropTypes.string,

  /** Copyright Year text for footer copyright */
  copyrightYear: PropTypes.string,

  /** Copyright Year aria text for AODA */
  copyrightYearAria: PropTypes.string,

  /** Logo component. Recommended to use with `@manulife/mux-logos` package */
  logo: PropTypes.node,

  /** Quick Links options */
  quickLinks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    href: PropTypes.string.isRequired,
    isExternal: PropTypes.bool
  })),

  /** List of social media links to display */
  socialMediaLinks: PropTypes.arrayOf(PropTypes.shape({
    site: PropTypes.oneOf([SOCIAL_MEDIA.FACEBOOK, SOCIAL_MEDIA.INSTAGRAM, SOCIAL_MEDIA.LINKEDIN, SOCIAL_MEDIA.TWITTER, SOCIAL_MEDIA.YOUTUBE]),
    href: PropTypes.string
  }))
} : void 0;
var dateYear = new Date().getFullYear();
Footer.defaultProps = {
  id: undefined,
  items: [],
  copyrightText: undefined,
  copyrightYear: "\xA9 1999-".concat(dateYear),
  copyrightYearAria: "\xA9 1999-".concat(dateYear),
  logo: undefined,
  quickLinks: undefined,
  socialMediaLinks: []
};
process.env.NODE_ENV !== "production" ? Footer.displayName = 'Footer' : void 0;
var Footer$1 = Footer;

export { Footer$1 as default };
